// "CW Squad" header button — hover/click opens live team status + my BRB controls.
import { useRef, useState } from "react";
import { Radio, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useSquad } from "./SquadContext";
import SquadAvatar, { STATUS } from "./SquadAvatar";
import MyStatusCard from "./MyStatusCard";
import SquadMemberDetail from "./SquadMemberDetail";

function Counts({ c = {} }) {
  return (
    <div className="grid grid-cols-3 px-4 py-3 border-b border-white/[0.06]" data-testid="squad-counts">
      {Object.entries(STATUS).map(([k, s]) => (
        <div key={k} className="text-center">
          <div className={`text-lg font-bold tabular-nums ${s.text}`} data-testid={`squad-count-${k}`}>{c[k] || 0}</div>
          <div className="text-[10px] text-slate-500">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function Grid({ members, onPick }) {
  return (
    <div className="grid grid-cols-4 gap-y-3 gap-x-1 p-4 max-h-[300px] overflow-y-auto" data-testid="squad-grid">
      {members.map((m) => (
        <button key={m.id} type="button" onClick={() => onPick(m)} data-testid={`squad-member-${m.id}`}
          className="flex flex-col items-center gap-1 rounded-lg py-1.5 hover:bg-white/[0.04] transition-colors min-w-0">
          <SquadAvatar name={m.name} url={m.avatar_url} status={m.status} size={38} />
          <span className="text-[11px] text-white truncate max-w-full px-1">{m.name.split(" ")[0]}{m.is_me ? " (you)" : ""}</span>
          <span className={`text-[9.5px] truncate max-w-full px-1 ${STATUS[m.status].text}`} data-testid={`squad-member-status-${m.id}`}>{STATUS[m.status].label}{m.status === "brb" && m.reason ? ` · ${m.reason}` : ""}</span>
        </button>
      ))}
    </div>
  );
}

export default function SquadButton() {
  const { me, squad } = useSquad() || {};
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(null);
  const t = useRef(null);
  const hover = (v) => { clearTimeout(t.current); t.current = setTimeout(() => setOpen(v), v ? 150 : 300); };
  const members = squad?.members || [];
  const live = members.filter((m) => m.status !== "offline").slice(0, 3);
  const picked = pick && members.find((m) => m.id === pick);
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPick(null); }}>
      <PopoverTrigger asChild>
        <button type="button" onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)} data-testid="squad-trigger"
          className={`inline-flex items-center gap-2 h-9 pl-2 pr-2.5 rounded-lg border transition-colors ${open ? "border-white/20 bg-white/[0.06]" : "border-white/10 hover:bg-white/[0.05]"}`}>
          <span className="relative">
            <Users className="w-4 h-4 text-orange-400" />
            <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-[#0F1A30] ${STATUS[me?.status || "offline"].dot}`} data-testid="squad-my-dot" />
          </span>
          <span className="hidden xl:inline text-[12px] font-semibold text-slate-200 whitespace-nowrap">CW Squad</span>
          <span className="hidden md:flex -space-x-2">
            {live.map((m) => <SquadAvatar key={m.id} name={m.name} url={m.avatar_url} size={22} />)}
          </span>
          <span className="text-[11px] font-mono text-emerald-300 tabular-nums" data-testid="squad-active-count">{squad?.counts?.active ?? "–"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} collisionPadding={8} onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)}
        className="w-[380px] max-w-[calc(100vw-16px)] p-0 overflow-hidden rounded-xl border-white/10 bg-[#0F1420]/95 backdrop-blur-xl text-white shadow-2xl" data-testid="squad-panel">
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div>
            <div className="font-display font-bold text-[15px]">CW Squad</div>
            <div className="text-[11px] text-slate-400">Who's available for live calls</div>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono text-emerald-300 bg-emerald-500/10 border border-emerald-400/30">
            <Radio className="w-3 h-3" /> Live
          </span>
        </div>
        {picked ? <SquadMemberDetail m={picked} onBack={() => setPick(null)} /> : (
          <>
            <MyStatusCard />
            <Counts c={squad?.counts} />
            <Grid members={members} onPick={(m) => setPick(m.id)} />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
